<script setup lang="ts">
import { useAuthStore } from '@/modules/auth/store/auth';
import { storeToRefs } from 'pinia';

const route = useRoute();
const { getUser } = storeToRefs(useAuthStore());

const navItems = [
  {
    label: 'Dashboard',
    to: '/tools',
    icon: 'mdi-view-dashboard-outline',
  },
  {
    label: 'Integrations',
    to: '/tools/integrations',
    icon: 'mdi-puzzle-outline',
  },
];

const isActive = (path: string) => {
  if (path === '/tools') {
    return route.path === '/tools';
  }
  return route.path.startsWith(path);
};
</script>

<template>
  <v-layout class="tools-layout">
    <!-- Sidebar -->
    <v-navigation-drawer
      permanent
      width="240"
      class="tools-sidebar"
      :elevation="0"
    >
      <!-- Logo + Label -->
      <div class="tools-sidebar__header">
        <NuxtLink to="/tools" class="tools-sidebar__logo-link">
          <img src="/logo.png" alt="Tradel" class="tools-sidebar__logo" />
        </NuxtLink>
        <span class="tools-sidebar__label">Tools</span>
      </div>

      <!-- Nav items -->
      <nav class="tools-sidebar__nav">
        <NuxtLink
          v-for="item in navItems"
          :key="item.to"
          :to="item.to"
          class="tools-sidebar__nav-item"
          :class="{ 'tools-sidebar__nav-item--active': isActive(item.to) }"
        >
          <v-icon size="18" class="tools-sidebar__nav-icon">{{ item.icon }}</v-icon>
          <span>{{ item.label }}</span>
        </NuxtLink>
      </nav>

      <!-- Bottom: user + back to site -->
      <div class="tools-sidebar__footer">
        <div class="tools-sidebar__user">
          <v-icon size="16" class="mr-1" color="#9CA3AF">mdi-account-outline</v-icon>
          <span class="tools-sidebar__username">{{ getUser?.full_name || getUser?.email || 'Admin' }}</span>
        </div>
        <NuxtLink to="/" class="tools-sidebar__back-link">
          <v-icon size="14" class="mr-1">mdi-arrow-left</v-icon>
          Back to site
        </NuxtLink>
      </div>
    </v-navigation-drawer>

    <!-- Main content -->
    <v-main class="tools-main">
      <NuxtPage />
    </v-main>
  </v-layout>
</template>

<style scoped>
.tools-layout {
  background: #0d0d0d;
  min-height: 100vh;
}

.tools-sidebar {
  background: #161616 !important;
  border-right: 1px solid #2a2a2a !important;
  display: flex;
  flex-direction: column;
}

.tools-sidebar__header {
  padding: 24px 20px 20px;
  border-bottom: 1px solid #2a2a2a;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.tools-sidebar__logo-link {
  display: inline-block;
}

.tools-sidebar__logo {
  height: 32px;
  width: auto;
}

.tools-sidebar__label {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #9ca3af;
}

.tools-sidebar__nav {
  padding: 12px 12px;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.tools-sidebar__nav-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 12px;
  border-radius: 6px;
  color: #9ca3af;
  font-size: 14px;
  font-weight: 500;
  text-decoration: none;
  transition: background 0.15s, color 0.15s;
  border-left: 3px solid transparent;
}

.tools-sidebar__nav-item:hover {
  background: #1c1c1c;
  color: #fafafa;
}

.tools-sidebar__nav-item--active {
  border-left-color: #f26b1d;
  color: #f26b1d;
  background: rgba(242, 107, 29, 0.08);
}

.tools-sidebar__nav-icon {
  flex-shrink: 0;
}

.tools-sidebar__footer {
  padding: 16px 20px;
  border-top: 1px solid #2a2a2a;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.tools-sidebar__user {
  display: flex;
  align-items: center;
  font-size: 13px;
  color: #9ca3af;
  overflow: hidden;
}

.tools-sidebar__username {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tools-sidebar__back-link {
  display: flex;
  align-items: center;
  font-size: 12px;
  color: #9ca3af;
  text-decoration: none;
  transition: color 0.15s;
}

.tools-sidebar__back-link:hover {
  color: #fafafa;
}

.tools-main {
  background: #0d0d0d;
  min-height: 100vh;
}
</style>
